import React, {useEffect, useState} from 'react';
import {Button, Card, Input, Modal, Form, Skeleton, Empty, Typography} from 'antd';
import {
    ExclamationCircleOutlined
} from '@ant-design/icons';
import {useTaskBoard} from './useTaskBoard';
import GoogleAnalyticsPropertySelect from "@/utils/GoogleAnalyticsPropertySelect";

function CreateOrEditBoard(props) {
    return <Modal
        title={props.modalState.type === "create" ? "Create New Board" : "Edit Board"}
        open={props.modalState.visible}
        onOk={props.onOk}
        onCancel={props.onCancel}
        confirmLoading={props.modalState.loading}
        destroyOnClose
        okButtonProps={{
            size: "large",
            className: "text-sm font-semibold leading-tight px-5 py-2.5 relative items-center rounded-full justify-center border border-gray-200 bg-[#3d8bfd]"
        }}
        cancelButtonProps={{
            size: "large",
            className: "text-sm font-semibold leading-tight px-5 py-2.5 mr-6 relative items-center rounded-full justify-center border border-gray-200"
        }}
        width={500}
        centered
    >
        <Form
            form={props.form}
            size={"small"}
            layout="vertical"
            className="mt-6"
            requiredMark={false}
            initialValues={{
                name: "",
                description: "",
                assigned_properties: [],
                manual_client_ids: [],
                group_ids: []
            }}
        >
            <Form.Item
                name="name"
                rules={[
                    {required: true, message: "Please enter board name"},
                    {max: 100, message: "Name cannot exceed 100 characters"}
                ]}
            >
                <Input
                    placeholder="Enter board name"
                    className="rounded-lg py-2.5 text-base"
                />
            </Form.Item>

            <Form.Item
                name="description"
                rules={[
                    {max: 1000, message: "Description cannot exceed 1000 characters"}
                ]}
            >
                <Input.TextArea
                    placeholder="Enter board description"
                    rows={4}
                    className="rounded-lg text-base"
                />
            </Form.Item>

            <Form.Item
                name="assignedTo"
            >
                <GoogleAnalyticsPropertySelect
                    name="google_analytics_property_id"
                    id="google_analytics_property_id"
                    className="gray_clr"
                    value={null}
                    onChangeCallback={props.onChangeCallback}
                    placeholder="Assign board to"
                    components={{
                        DropdownIndicator: props.dropdownIndicator,
                        IndicatorSeparator: props.dropdownIndicator
                    }}
                    multiple={false}
                />
            </Form.Item>

            {/* Hidden form items to store the parsed assignments */}
            <Form.Item name="assigned_properties" hidden>
                <Input type="hidden"/>
            </Form.Item>
            <Form.Item name="manual_client_ids" hidden>
                <Input type="hidden"/>
            </Form.Item>
            <Form.Item name="group_ids" hidden>
                <Input type="hidden"/>
            </Form.Item>
        </Form>
    </Modal>;
}

const TaskBoard = ({upgradePopup, user}) => {
    console.log();
    const [form] = Form.useForm();
    const {
        boards,
        currentBoard,
        modalState,
        pageLoading,
        deleteLoading,
        createBoard,
        updateBoard,
        deleteBoard,
        handleModalOpen,
        handleModalClose
    } = useTaskBoard();

    const [expandedMap, setExpandedMap] = useState({});

    // Existing useEffect and handlers remain the same
    useEffect(() => {
        if (!modalState.visible) {
            form.resetFields();
        }
    }, [modalState.visible, form]);

    useEffect(() => {
        if (user?.price_plan && !user?.price_plan?.has_task_management) {
            upgradePopup();
            return true;
        }
    }, [user]);

    const parseAssignments = (selectedValues) => {
        const assignments = {
            assigned_properties: [],
            manual_client_ids: [],
            group_ids: []
        };

        selectedValues.forEach(value => {
            if (typeof value === 'number') {
                // If it's a number, it's a property ID
                assignments.assigned_properties.push(value);
            } else if (typeof value === 'string') {
                // Parse string values like "1_manual" or "16_group"
                const [id, type] = value.split('_');

                if (type === 'manual') {
                    assignments.manual_client_ids.push(parseInt(id));
                } else if (type === 'group') {
                    assignments.group_ids.push(parseInt(id));
                }
            }
        });

        return assignments;
    };

    const handleModalSubmit = async () => {
        try {
            if (!user?.price_plan?.has_task_management) {
                upgradePopup();
                return true;
            }
            const values = await form.validateFields();

            // Get the assignments from the form
            const assignments = {
                assigned_properties: values.assigned_properties || [],
                manual_client_ids: values.manual_client_ids || [],
                group_ids: values.group_ids || []
            };

            // Merge the assignments with other form values
            const formData = {
                ...values,
                ...assignments
            };

            if (modalState.type === 'create') {
                await createBoard(formData);
            } else {
                await updateBoard(currentBoard.id, formData);
            }
            form.resetFields();
        } catch (error) {
            if (error.errorFields) return;
        }
    };

    const handleDeleteBoard = (board) => {
        Modal.confirm({
            title: 'Delete Board',
            icon: <ExclamationCircleOutlined/>,
            content: `Are you sure you want to delete "${board.name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => deleteBoard(board.id)
        });
    };

    const handleEditBoard = (board) => {
        // Combine all assignments into a single array for the select
        const assignedValues = [
            ...(board.assigned_properties || []),
            ...(board.manual_client_ids || []).map(id => `${id}_manual`),
            ...(board.group_ids || []).map(id => `${id}_group`)
        ];

        form.setFieldsValue({
            name: board.name,
            description: board.description,
            assignedTo: assignedValues,
            assigned_properties: board.assigned_properties || [],
            manual_client_ids: board.manual_client_ids || [],
            group_ids: board.group_ids || []
        });
        handleModalOpen('edit', board);
    };

    const handleOpenBoard = (board) => {
        window.location.href = (`/tm/ui-boards/${board.id}`);
    };
    const assignedToChangeHandler = (ev) => {
        const assignments = parseAssignments([ev.target.value]);

        // Update form values with parsed assignments
        form.setFieldsValue({
            assigned_properties: assignments.assigned_properties,
            manual_client_ids: assignments.manual_client_ids,
            group_ids: assignments.group_ids
        });
    };

    if (pageLoading) {
        return (
            <div className="min-h-screen p-8 animate-fade-in">
                <Skeleton active className="max-w-7xl mx-auto"/>
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8 max-w-7xl mx-auto">
                    {[1, 2, 3, 4].map(i => (
                        <Card key={i} className="shadow-sm hover:shadow-md transition-all duration-300 animate-pulse">
                            <Skeleton active/>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    if (user?.price_plan && !user?.price_plan?.has_task_management) {
        return <div className={"h-full flex items-center justify-center"}>You need to upgrade your plan to use this
            feature</div>
    }

    return (
        <div className="max-w-7xl mx-auto pt-24 animate-fade-in">
            <div className="relative p-0">
                <div className="pageHead pb-8">
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="pageTitle m-0"> Task Boards</h2>
                        <div className="">
                            {/* <span className='text-white'>Create Board</span> */}
                            <Button
                                className={"rounded-md shadow-2xl overflow-hidden"}
                                type={"primary"}
                                onClick={() => handleModalOpen('create')}
                            >
                                Create Board
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Enhanced Boards Grid */}
                <div className="max-w-7xl mx-auto animate-fade-in-up">
                    {boards.length === 0 ? (
                        <Empty
                            description={
                                <div className="flex flex-col items-center gap-4">
                                <span className="text-gray-600 text-lg font-medium">
                                    No boards yet
                                </span>
                                    <span className="text-gray-500">
                                    Create your first board to get started
                                </span>
                                </div>
                            }
                            className="bg-white/80 backdrop-blur-sm p-16 rounded-2xl shadow-md border border-white/20"
                        />
                    ) : (
                        <>
                            {boards.map((board) => {
                                return (
                                    <div
                                        className={"row-wrapper mt-2 gaa-tmBordList"}
                                        key={board.id}
                                        onClick={() => handleOpenBoard(board)}
                                    >
                                        <div
                                            className={`flex border border-gray-200 rounded-xl shadow relative items-start sm:items-center p-6 cursor-pointer`}
                                            key={board.id}>
                                            <div className="description d-flex flex-column w-full">
                                                <Typography.Title className={'text-[#096db7]'} level={5}>
                                                    {board.name}
                                                    <span className="mx-2">|</span>
                                                    {board.tasks_count || 'No'} Tasks
                                                    <span className="mx-2">|</span>
                                                    {board.lists_count || 'No'} Lists
                                                </Typography.Title>
                                                {/* <p className="titleCategory m-0 color-blue font-600">

                                                </p>*/}
                                                <Typography.Paragraph
                                                    ellipsis={{
                                                        rows: 2,
                                                        expandable: 'collapsible',
                                                        expanded: expandedMap[board.id],
                                                        onExpand: (_, {expanded}) =>
                                                            setExpandedMap(prev => ({
                                                                ...prev,
                                                                [board.id]: expanded
                                                            }))
                                                    }}
                                                    className="mb-0 text-gray-600 text-normal font-medium"
                                                >
                                                    {board.description || ''}
                                                </Typography.Paragraph>
                                            </div>
                                            <div className="w-[300px]">
                                                <Typography.Title className={'text-[#666666] text-left sm:text-center'} level={5}>
                                                    {board.assignment_options?.[0]?.label || 'All Properties'}
                                                </Typography.Title>
                                            </div>
                                            <div className="w-[220px]">
                                                <ul className="flex justify-start sm:justify-end list-unstyled">
                                                    {/*<li>
                                                        <Button type={"text"}>
                                                            <img onClick={() => handleOpenBoard(board)}
                                                                 src={`/icon-eye-open.svg`}/>
                                                        </Button>
                                                    </li>*/}
                                                    <li className={"shrink-0"}>
                                                        <Button onClick={(ev) => {
                                                            ev.stopPropagation();
                                                            handleEditBoard(board)
                                                        }} type={"text"}>
                                                            <img src={`/icon-edit.svg`}/>
                                                        </Button>
                                                    </li>
                                                    <li className={"shrink-0"}>
                                                        <Button loading={deleteLoading}
                                                                disabled={deleteLoading === board.id}
                                                                onClick={(ev) => {
                                                                    ev.stopPropagation();
                                                                    handleDeleteBoard(board)
                                                                }}
                                                                className={"text-red-600"} type={"text"}>
                                                            <img src={`/icon-trash.svg`}/>
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </>
                    )}
                </div>

                {/* Enhanced Modal */}
                <CreateOrEditBoard
                    modalState={modalState}
                    onOk={handleModalSubmit}
                    onCancel={handleModalClose}
                    form={form}
                    onChangeCallback={assignedToChangeHandler}
                    dropdownIndicator={() => null}
                />
            </div>
        </div>
    );
};

export default TaskBoard;
